import Controller from "@ember/controller";
import { action } from "@ember/object";
import { tracked } from "@glimmer/tracking";
import { ajax } from "discourse/lib/ajax";
import { htmlSafe } from "@ember/template";
import { TEST_TYPES, REGIONS } from "../lib/benchmark-constants";

function grade(ms) {
  if (ms === null || ms === undefined) return { letter: "—", cls: "" };
  if (ms < 200) return { letter: "A", cls: "pd-benchmark__grade--a" };
  if (ms < 500) return { letter: "B", cls: "pd-benchmark__grade--b" };
  if (ms < 1000) return { letter: "C", cls: "pd-benchmark__grade--c" };
  if (ms < 2000) return { letter: "D", cls: "pd-benchmark__grade--d" };
  return { letter: "F", cls: "pd-benchmark__grade--f" };
}

function responseBarPercent(ms) {
  if (!ms) return 0;
  const clamped = Math.min(Math.max(ms, 0), 2000);
  return Math.round(((2000 - clamped) / 2000) * 100);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default class ProxyviewsBenchmarksController extends Controller {
  @tracked runs = [];
  @tracked meta = {};
  @tracked loadError = false;
  @tracked loading = false;
  @tracked selectedRuns = [];

  testTypes = TEST_TYPES;
  regions = REGIONS;

  @tracked testType = "";
  @tracked region = "";
  @tracked providerFilter = "";
  @tracked sortBy = "response";
  @tracked page = 1;

  get totalPages() {
    return this.meta.total_pages || 1;
  }

  get hasNextPage() {
    return this.page < this.totalPages;
  }
  get hasPrevPage() {
    return this.page > 1;
  }

  get formattedLatestRunDate() {
    const raw = this.meta?.latest_run_date;
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  get avgResponseTimeDisplay() {
    const ms = this.meta?.avg_response_time_ms;
    if (ms === null || ms === undefined) return null;
    return `${Math.round(ms)}ms`;
  }

  get providerList() {
    const names = this.runs.map((r) => r.provider_name).filter(Boolean);
    return [...new Set(names)].sort();
  }

  buildQueryParams() {
    const params = { per_page: 20, page: this.page };
    if (this.testType) params.test_type = this.testType;
    if (this.region) params.region = this.region;
    return params;
  }

  get sortedRuns() {
    let runs = [...this.runs];

    if (this.providerFilter) {
      runs = runs.filter((r) => r.provider_name === this.providerFilter);
    }

    switch (this.sortBy) {
      case "response":
        runs.sort(
          (a, b) => (a.avg_response_time_ms || 99999) - (b.avg_response_time_ms || 99999)
        );
        break;
      case "success":
        runs.sort((a, b) => (b.success_rate || 0) - (a.success_rate || 0));
        break;
      case "grade":
        runs.sort(
          (a, b) => (a.avg_response_time_ms || 99999) - (b.avg_response_time_ms || 99999)
        );
        break;
      case "provider":
        runs.sort((a, b) =>
          (a.provider_name || "").localeCompare(b.provider_name || "")
        );
        break;
      case "date":
        runs.sort(
          (a, b) =>
            new Date(b.tested_at || 0).getTime() -
            new Date(a.tested_at || 0).getTime()
        );
        break;
    }

    return runs;
  }

  async loadBenchmarks() {
    this.loading = true;
    let data;
    try {
      data = await ajax("/benchmarks.json", {
        data: this.buildQueryParams(),
      });
    } catch (e) {
      this.runs = [];
      this.meta = {};
      this.loadError = true;
      this.loading = false;
      return;
    }

    const runs = (data.benchmark_runs || [])
      .map((r) => r.benchmark_run || r)
      .map((r) => {
        const g = grade(r.avg_response_time_ms);
        const respPct = responseBarPercent(r.avg_response_time_ms);
        return {
          ...r,
          gradeLetter: g.letter,
          gradeClass: g.cls,
          responseGradeClass: g.cls,
          sourceClass:
            r.source_type === "community"
              ? "pd-benchmark__source-badge--community"
              : "pd-benchmark__source-badge--admin",
          sourceLabel: r.source_type === "community" ? "Community" : "Admin",
          responseDisplay: r.avg_response_time_ms
            ? `${Number(r.avg_response_time_ms).toFixed(0)}ms`
            : "—",
          successDisplay:
            r.success_rate !== null && r.success_rate !== undefined
              ? `${r.success_rate}%`
              : "—",
          blockDisplay:
            r.block_rate !== null && r.block_rate !== undefined
              ? `${r.block_rate}%`
              : "—",
          successStyle: htmlSafe(`width: ${r.success_rate || 0}%`),
          blockStyle: htmlSafe(`width: ${r.block_rate || 0}%`),
          responseStyle: htmlSafe(`width: ${respPct}%`),
          tested_at_display: formatDate(r.tested_at),
          selected: this.selectedRuns.some((s) => s.id === r.id),
        };
      });
    this.runs = runs;
    this.meta = data.meta || {};
    this.loadError = false;
    this.loading = false;
  }

  @action
  filterByTestType(event) {
    this.testType = event.target.value;
    this.page = 1;
    this.loadBenchmarks();
  }

  @action
  filterByRegion(event) {
    this.region = event.target.value;
    this.page = 1;
    this.loadBenchmarks();
  }

  @action
  filterByProvider(event) {
    this.providerFilter = event.target.value;
  }

  @action
  changeSortBy(event) {
    this.sortBy = event.target.value;
  }

  @action
  toggleRunSelection(run) {
    const idx = this.selectedRuns.findIndex((s) => s.id === run.id);
    if (idx >= 0) {
      this.selectedRuns = this.selectedRuns.filter((s) => s.id !== run.id);
    } else {
      if (this.selectedRuns.length >= 4) return; // max 4 for comparison
      this.selectedRuns = [...this.selectedRuns, run];
    }
    this.runs = this.runs.map((r) => ({
      ...r,
      selected: this.selectedRuns.some((s) => s.id === r.id),
    }));
  }

  @action
  openCompare() {
    if (this.selectedRuns.length < 2) return;
    const slugs = [
      ...new Set(this.selectedRuns.map((r) => r.provider_slug).filter(Boolean)),
    ];
    this.transitionToRoute("proxyviews-benchmarks-compare", {
      queryParams: { providers: slugs.join(",") },
    });
  }

  @action
  nextPage() {
    if (!this.hasNextPage || this.loading) return;
    this.page += 1;
    this.loadBenchmarks();
  }

  @action
  prevPage() {
    if (!this.hasPrevPage || this.loading) return;
    this.page -= 1;
    this.loadBenchmarks();
  }

  @action
  handleLogoError(event) {
    event.target.style.display = "none";
    const fallback = event.target.nextElementSibling;
    if (fallback) {
      fallback.style.display = "flex";
    }
  }
}
